import { FlowApiError } from '../../lib/api';

/**
 * ماذا نفعل بنتيجة مسبار `/auth/session`؟ — **قرارٌ نقيّ**.
 *
 * ## العطب الذي أوجد هذا الملف، مقيسًا
 *
 * كان الحارس يكتب `authenticated = !isError && data?.authenticated === true`،
 * فيحوّل إلى `/login` عند **أي** خطأ. وتعليقه يقول «401 جواب نهائي لا خطأ عابر
 * يستحق إعادة المحاولة» — وهو صحيحٌ عن 401 وحدها، لكن الكود طبّقه على الجميع.
 *
 * وقِيس في متصفح على بناء إنتاج: مسحٌ سريع لإحدى وعشرين شاشة تجاوز حدّ
 * 300 طلب/دقيقة، فردّ الخادم **36 مرة** بـ`FLOW_RATE_LIMITED`، ووجد المالك
 * نفسه على شاشة الدخول وجلسته سليمة تمامًا.
 *
 * **وليس المسح وحده يبلغ ذلك الحدّ**: عودة PWA من انقطاع تُعيد جلب كل ما بطل
 * دفعةً، وإعادة اتصال SSE مثلها. وإعادة تشغيل الخادم لثوانٍ تعطي 502. في كل
 * تلك الحالات كان يُطرد إلى الدخول ويُطالَب بكلمة مروره بلا سبب.
 *
 * ## القاعدة
 *
 * **401 وحدها تعني «لست داخلًا».** وما عداها يعني «لا أعرف» — لا يُحوَّل، ولا
 * تُرسم القشرة (فأبناؤها سيطلبون بدورهم فيفشلون)، بل تُعاد المحاولة.
 */
export type SessionDecision =
  /** المسبار جارٍ — سكونٌ لا وميض. */
  | { readonly kind: 'checking' }
  /** جلسةٌ صالحة — تُرسم القشرة. */
  | { readonly kind: 'authenticated' }
  /** 401 صريح أو `authenticated: false` — إلى `/login`. */
  | { readonly kind: 'unauthenticated' }
  /** خطأ ليس 401 — لا طرد ولا رسم؛ رسالةٌ تقول ما حدث بالتحديد. */
  | { readonly kind: 'unavailable'; readonly messageAr: string };

/** رسالة تصف **ما حدث** لا «فشل» — والحدّ والانقطاع والعطل ثلاثة أشياء. */
export function unavailableMessageAr(error: unknown): string {
  if (error instanceof FlowApiError) {
    if (error.isOffline) return 'لا اتصال بالخادم — سنعيد المحاولة تلقائيًّا.';
    if (error.status === 429) {
      return 'طلبات كثيرة في وقت قصير — جلستك سليمة، وسنعيد المحاولة بعد قليل.';
    }
    if (error.status >= 500) {
      return `الخادم لم يستطع تأكيد الجلسة (${String(error.status)}) — سنعيد المحاولة.`;
    }
    return `تعذّر تأكيد الجلسة (${String(error.status)}) — سنعيد المحاولة.`;
  }
  return 'تعذّر تأكيد الجلسة — سنعيد المحاولة.';
}

export function decideSession(input: {
  readonly isPending: boolean;
  readonly error: unknown;
  readonly authenticated: boolean | undefined;
}): SessionDecision {
  if (input.isPending) return { kind: 'checking' };

  if (input.error != null) {
    /* 401 وحدها حكمٌ نهائي — وغيرها لا يُترجم خروجًا. */
    if (input.error instanceof FlowApiError && input.error.isUnauthorized) {
      return { kind: 'unauthenticated' };
    }
    return { kind: 'unavailable', messageAr: unavailableMessageAr(input.error) };
  }

  return input.authenticated === true
    ? { kind: 'authenticated' }
    : { kind: 'unauthenticated' };
}

/**
 * إعادة المحاولة: **لا** على 401، ونعم على ما عداه حتى ثلاث محاولات.
 *
 * وثلاث لا لانهائية: مسبارٌ يدور أبدًا على خادمٍ ساقط يستنزف الحدّ نفسه الذي
 * أوقعنا في العطب.
 */
export function shouldRetrySession(failureCount: number, error: unknown): boolean {
  if (error instanceof FlowApiError && error.isUnauthorized) return false;
  return failureCount < 3;
}
